// src/components/production/ProductionStageTracker.jsx

import React, { useState } from 'react';
import { logStepCompleted, logMaterialDeducted, logProductAdded, logWasteRecorded } from '../../utils/productionOrderActivityLogger';
import { calculateSubcontractingCost } from '../../utils/subcontractorUtils';
import { doc, updateDoc, serverTimestamp, setDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';

const styles = {
    stageContainer: {
        // Removed background and border for cleaner look within accordion
        padding: '0 0 20px 0',
        marginBottom: '20px',
        borderBottom: '1px solid #f1f5f9'
    },
    stageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    stageTitle: {
        fontSize: '16px',
        fontWeight: '600',
        margin: 0
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
    },
    pendingBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    inProgressBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    completedBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    cancelledBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    stageDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        marginBottom: '15px'
    },
    detailItem: {
        marginBottom: '10px'
    },
    detailLabel: {
        fontSize: '12px',
        color: '#64748b',
        marginBottom: '4px'
    },
    detailValue: {
        fontSize: '14px',
        fontWeight: '500'
    },
    materialsList: {
        listStyle: 'none',
        padding: 0,
        margin: '10px 0'
    },
    materialItem: {
        padding: '8px 0',
        borderBottom: '1px solid #f1f5f9'
    },
    completeButton: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '14px'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: 'white',
        padding: '30px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    }
};

function ProductionStageTracker({
    stage,
    companyId,
    orderId,
    user,
    onComplete,
    rawMaterials,
    products,
    subcontractors, // Changed from single subcontractor to array
    productionOrder
}) {
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completionData, setCompletionData] = useState({
        completedQuantity: stage.outputProduced?.quantity || 0,
        wasteQuantity: 0,
        notes: ''
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span style={{ ...styles.statusBadge, ...styles.pendingBadge }}>Bekliyor</span>;
            case 'inProgress':
                return <span style={{ ...styles.statusBadge, ...styles.inProgressBadge }}>Devam Ediyor</span>;
            case 'completed':
                return <span style={{ ...styles.statusBadge, ...styles.completedBadge }}>Tamamlandı</span>;
            case 'cancelled':
                return <span style={{ ...styles.statusBadge, ...styles.cancelledBadge }}>İptal Edildi</span>;
            default:
                return <span style={{ ...styles.statusBadge, backgroundColor: '#e2e8f0', color: '#64748b' }}>{status}</span>;
        }
    };

    const handleCompleteStage = async () => {
        try {
            // Calculate total completed quantity from subcontractors
            let totalCompletedQuantity = completionData.completedQuantity;
            if (stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0) {
                totalCompletedQuantity = stage.subcontractorDistribution.reduce((sum, dist) => {
                    return sum + (dist.completedQuantity || 0);
                }, 0);
            }

            // Log the completion
            await logStepCompleted(companyId, orderId, user, {
                name: stage.name,
                index: stage.sequence,
                completedQuantity: totalCompletedQuantity,
                totalQuantity: stage.outputProduced?.quantity || 0,
                workCenter: stage.workCenter,
                userRole: user.role
            });

            // Log material deductions
            for (const material of stage.inputConsumed) {
                await logMaterialDeducted(companyId, orderId, user, {
                    name: material.material,
                    quantity: material.quantity,
                    unit: material.unit,
                    userRole: user.role
                });
            }

            // Log product addition
            await logProductAdded(companyId, orderId, user, {
                name: stage.outputProduced.product,
                quantity: totalCompletedQuantity,
                unit: stage.outputProduced.unit,
                userRole: user.role
            });

            // Log waste if any
            if (completionData.wasteQuantity > 0) {
                await logWasteRecorded(companyId, orderId, user, {
                    name: stage.outputProduced.product,
                    quantity: completionData.wasteQuantity,
                    unit: stage.outputProduced.unit,
                    stepName: stage.name,
                    userRole: user.role
                });
            }

            // Record subcontractor costs if this is a subcontracting stage
            if ((stage.subcontractor || (stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0) || (stage.subcontractors && stage.subcontractors.length > 0))) {
                await recordSubcontractorCosts();
            }

            // Call the onComplete callback
            onComplete(stage.id, {
                ...completionData,
                status: 'completed', // Always mark as completed when using the modal
                endTime: new Date()
            });

            setShowCompleteModal(false);
        } catch (error) {
            console.error('Error completing stage:', error);
            alert('Aşama tamamlanırken bir hata oluştu: ' + error.message);
        }
    };

    const recordSubcontractorCosts = async () => {
        try {
            // Handle multiple subcontractors with distribution
            if (stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0) {
                for (const dist of stage.subcontractorDistribution) {
                    if (dist.status === 'completed' && dist.completedQuantity > 0) {
                        const subcontractor = subcontractors.find(s => s.id === dist.supplierId);
                        if (subcontractor) {
                            // Calculate cost based on completed quantity
                            const tariffCost = calculateSubcontractingCost(
                                subcontractor,
                                stage.outputProduced.unit,
                                dist.completedQuantity
                            );

                            // Add labor cost defined in recipe
                            const laborCost = (dist.laborCost || 0) * dist.completedQuantity;
                            const totalCost = tariffCost + laborCost;

                            if (totalCost > 0) {
                                // Update subcontractor balance (negative because it's a cost)
                                const newBalance = (subcontractor.balance || 0) - totalCost;

                                // Update subcontractor in database
                                const subcontractorRef = doc(db, 'companies', companyId, 'subcontractors', subcontractor.id);
                                await updateDoc(subcontractorRef, {
                                    balance: newBalance,
                                    updatedAt: serverTimestamp()
                                });

                                // Create accounting entry for the subcontractor cost
                                const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
                                await setDoc(transactionRef, {
                                    type: 'Ödeme', // This represents a debt/payment to subcontractor
                                    amount: totalCost,
                                    description: `Fason maliyeti - ${stage.name} (${stage.outputProduced.product}) - ${dist.completedQuantity} ${stage.outputProduced.unit} (Tarife: ${tariffCost.toFixed(2)} ₺ + İşçilik: ${laborCost.toFixed(2)} ₺)`,
                                    relatedPartyId: subcontractor.id,
                                    relatedPartyName: subcontractor.name,
                                    date: serverTimestamp(),
                                    categoryId: 'production-cost', // Optional category for filtering
                                    orderId: orderId, // Link to the production order
                                    stageId: stage.id // Link to the production stage
                                });

                                console.log(`Subcontractor ${subcontractor.name} balance updated by -${totalCost.toFixed(2)} TRY (Tariff: ${tariffCost.toFixed(2)} + Labor: ${laborCost.toFixed(2)})`);
                            }
                        }
                    }
                }
            }
            // Handle single subcontractor (old format)
            else if (stage.subcontractor) {
                const subcontractor = subcontractors.find(s => s.id === stage.subcontractor.supplierId);
                if (subcontractor) {
                    // Calculate cost based on output quantity
                    const tariffCost = calculateSubcontractingCost(
                        subcontractor,
                        stage.outputProduced.unit,
                        stage.outputProduced.quantity
                    );

                    // Add labor cost defined in recipe
                    const laborCost = (stage.subcontractor.laborCost || 0) * stage.outputProduced.quantity;
                    const totalCost = tariffCost + laborCost;

                    if (totalCost > 0) {
                        // Update subcontractor balance (negative because it's a cost)
                        const newBalance = (subcontractor.balance || 0) - totalCost;

                        // Update subcontractor in database
                        const subcontractorRef = doc(db, 'companies', companyId, 'subcontractors', subcontractor.id);
                        await updateDoc(subcontractorRef, {
                            balance: newBalance,
                            updatedAt: serverTimestamp()
                        });

                        // Create accounting entry for the subcontractor cost
                        const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
                        await setDoc(transactionRef, {
                            type: 'Ödeme', // This represents a debt/payment to subcontractor
                            amount: totalCost,
                            description: `Fason maliyeti - ${stage.name} (${stage.outputProduced.product}) - ${stage.outputProduced.quantity} ${stage.outputProduced.unit} (Tarife: ${tariffCost.toFixed(2)} ₺ + İşçilik: ${laborCost.toFixed(2)} ₺)`,
                            relatedPartyId: subcontractor.id,
                            relatedPartyName: subcontractor.name,
                            date: serverTimestamp(),
                            categoryId: 'production-cost', // Optional category for filtering
                            orderId: orderId, // Link to the production order
                            stageId: stage.id // Link to the production stage
                        });

                        console.log(`Subcontractor ${subcontractor.name} balance updated by -${totalCost.toFixed(2)} TRY (Tariff: ${tariffCost.toFixed(2)} + Labor: ${laborCost.toFixed(2)})`);
                    }
                }
            }
            // Handle multiple subcontractors from recipe (fallback)
            else if (stage.subcontractors && stage.subcontractors.length > 0) {
                for (const sub of stage.subcontractors) {
                    const subcontractor = subcontractors.find(s => s.id === sub.supplierId);
                    if (subcontractor) {
                        // Calculate cost based on output quantity
                        const tariffCost = calculateSubcontractingCost(
                            subcontractor,
                            stage.outputProduced.unit,
                            stage.outputProduced.quantity
                        );

                        // Add labor cost defined in recipe
                        const laborCost = (sub.laborCost || 0) * stage.outputProduced.quantity;
                        const totalCost = tariffCost + laborCost;

                        if (totalCost > 0) {
                            // Update subcontractor balance (negative because it's a cost)
                            const newBalance = (subcontractor.balance || 0) - totalCost;

                            // Update subcontractor in database
                            const subcontractorRef = doc(db, 'companies', companyId, 'subcontractors', subcontractor.id);
                            await updateDoc(subcontractorRef, {
                                balance: newBalance,
                                updatedAt: serverTimestamp()
                            });

                            // Create accounting entry for the subcontractor cost
                            const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
                            await setDoc(transactionRef, {
                                type: 'Ödeme', // This represents a debt/payment to subcontractor
                                amount: totalCost,
                                description: `Fason maliyeti - ${stage.name} (${stage.outputProduced.product}) - ${stage.outputProduced.quantity} ${stage.outputProduced.unit} (Tarife: ${tariffCost.toFixed(2)} ₺ + İşçilik: ${laborCost.toFixed(2)} ₺)`,
                                relatedPartyId: subcontractor.id,
                                relatedPartyName: subcontractor.name,
                                date: serverTimestamp(),
                                categoryId: 'production-cost', // Optional category for filtering
                                orderId: orderId, // Link to the production order
                                stageId: stage.id // Link to the production stage
                            });

                            console.log(`Subcontractor ${subcontractor.name} balance updated by -${totalCost.toFixed(2)} TRY (Tariff: ${tariffCost.toFixed(2)} + Labor: ${laborCost.toFixed(2)})`);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error recording subcontractor costs:', error);
            alert('Fason tedarikçi maliyetleri kaydedilirken bir hata oluştu: ' + error.message);
        }
    };

    return (
        <div style={styles.stageContainer}>
            <div style={styles.stageHeader}>
                <h4 style={styles.stageTitle}>
                    {stage.sequence}. {stage.name}
                    {(stage.subcontractor || (stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0) || (stage.subcontractors && stage.subcontractors.length > 0)) && (
                        <span style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            color: '#06C167',
                            fontWeight: 'normal'
                        }}>
                            (Fason)
                        </span>
                    )}
                </h4>
                <div>
                    {getStatusBadge(stage.status)}
                    {stage.status === 'pending' && (
                        <button
                            onClick={() => {
                                // Update the stage status to inProgress
                                const updatedStage = { ...stage, status: 'inProgress', startTime: new Date() };
                                onComplete(stage.id, updatedStage);
                            }}
                            style={{
                                ...styles.completeButton,
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                marginLeft: '10px'
                            }}
                        >
                            Aşamayı Başlat
                        </button>
                    )}
                    {stage.status === 'inProgress' && (
                        <button
                            onClick={() => setShowCompleteModal(true)}
                            style={{
                                ...styles.completeButton,
                                marginLeft: '10px'
                            }}
                        >
                            Aşamayı Tamamla
                        </button>
                    )}
                </div>
            </div>

            <div style={styles.stageDetails}>
                <div>
                    <div style={styles.detailItem}>
                        <div style={styles.detailLabel}>Atölye</div>
                        <div style={styles.detailValue}>{stage.workCenter || '-'}</div>
                    </div>
                    <div style={styles.detailItem}>
                        <div style={styles.detailLabel}>İşçi</div>
                        <div style={styles.detailValue}>{stage.assignedWorker || '-'}</div>
                    </div>
                </div>

                <div>
                    <div style={styles.detailItem}>
                        <div style={styles.detailLabel}>Çıktı Ürünü</div>
                        <div style={styles.detailValue}>
                            {stage.outputProduced.quantity} {stage.outputProduced.unit} {stage.outputProduced.product}
                        </div>
                    </div>
                    <div style={styles.detailItem}>
                        <div style={styles.detailLabel}>Tahmini Süre</div>
                        <div style={styles.detailValue}>{stage.duration || 0} dakika</div>
                    </div>
                </div>
            </div>

            <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Girdi Hammaddeleri</div>
                {stage.inputConsumed.length > 0 ? (
                    <ul style={styles.materialsList}>
                        {stage.inputConsumed.map((material, index) => (
                            <li key={index} style={styles.materialItem}>
                                {material.quantity} {material.unit} {material.material}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>-</div>
                )}
            </div>

            {(stage.subcontractor || (stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0) || (stage.subcontractors && stage.subcontractors.length > 0)) && (
                <div style={{
                    background: '#e2e8f0',
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '15px'
                }}>
                    <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                    }}>
                        Fason Bilgileri
                    </h4>

                    {/* Display subcontractor distribution if available */}
                    {stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0 ? (
                        <div>
                            {stage.subcontractorDistribution.map((dist, index) => {
                                const subcontractor = subcontractors.find(s => s.id === dist.supplierId);
                                // Check if this subcontractor's portion is completed
                                const isCompleted = dist.status === 'completed';

                                return (
                                    <div key={dist.supplierId} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 0',
                                        borderBottom: index < stage.subcontractorDistribution.length - 1 ? '1px solid #cbd5e1' : 'none'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: '500' }}>
                                                {subcontractor ? subcontractor.name : dist.supplierName}
                                            </div>
                                            {dist.laborCost > 0 && (
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    İşçilik: {dist.laborCost.toFixed(2)} ₺/adet
                                                    {dist.completedQuantity > 0 && (
                                                        <div>
                                                            Toplam İşçilik: {(dist.laborCost * dist.completedQuantity).toFixed(2)} ₺
                                                            ({dist.completedQuantity} adet)
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {stage.status === 'inProgress' ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        value={dist.completedQuantity || 0}
                                                        onChange={(e) => {
                                                            // Update the subcontractor distribution completed quantity
                                                            const updatedStage = { ...stage };
                                                            const updatedDistribution = [...stage.subcontractorDistribution];
                                                            updatedDistribution[index] = {
                                                                ...dist,
                                                                completedQuantity: parseFloat(e.target.value) || 0
                                                            };
                                                            updatedStage.subcontractorDistribution = updatedDistribution;
                                                            onComplete(stage.id, updatedStage);
                                                        }}
                                                        style={{
                                                            width: '60px',
                                                            padding: '4px',
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '4px',
                                                            textAlign: 'center'
                                                        }}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <span style={{ fontWeight: '500' }}>
                                                        / {dist.quantity} {stage.outputProduced.unit}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            // Update the subcontractor distribution status
                                                            const updatedStage = { ...stage };
                                                            const updatedDistribution = [...stage.subcontractorDistribution];
                                                            updatedDistribution[index] = {
                                                                ...dist,
                                                                status: isCompleted ? 'inProgress' : 'completed',
                                                                completedAt: isCompleted ? null : new Date()
                                                            };
                                                            updatedStage.subcontractorDistribution = updatedDistribution;
                                                            onComplete(stage.id, updatedStage);
                                                        }}
                                                        style={{
                                                            padding: '4px 8px',
                                                            background: isCompleted ? '#ef4444' : '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {isCompleted ? 'Tamamlanmadı' : 'Tamamla'}
                                                    </button>
                                                </>
                                            ) : (
                                                <div style={{ fontWeight: '500' }}>
                                                    {dist.completedQuantity || 0} / {dist.quantity} {stage.outputProduced.unit}
                                                    {isCompleted && (
                                                        <span style={{
                                                            background: '#10b981',
                                                            color: 'white',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            marginLeft: '8px'
                                                        }}>
                                                            Tamamlandı
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : stage.subcontractor ? (
                        // Handle single subcontractor (old format)
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 0'
                        }}>
                            <div>
                                <div style={{ fontWeight: '500' }}>
                                    {stage.subcontractor.supplierName}
                                </div>
                                {stage.subcontractor.laborCost > 0 && (
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        İşçilik: {stage.subcontractor.laborCost.toFixed(2)} ₺/adet
                                        {stage.outputProduced.quantity > 0 && (
                                            <div>
                                                Toplam İşçilik: {(stage.subcontractor.laborCost * stage.outputProduced.quantity).toFixed(2)} ₺
                                                ({stage.outputProduced.quantity} adet)
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div style={{ fontWeight: '500' }}>
                                {stage.outputProduced.quantity} {stage.outputProduced.unit}
                            </div>
                        </div>
                    ) : stage.subcontractors && stage.subcontractors.length > 0 ? (
                        // Handle multiple subcontractors from recipe (fallback)
                        <div>
                            {stage.subcontractors.map((sub, index) => {
                                const subcontractor = subcontractors.find(s => s.id === sub.supplierId);
                                return (
                                    <div key={sub.supplierId} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 0',
                                        borderBottom: index < stage.subcontractors.length - 1 ? '1px solid #cbd5e1' : 'none'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: '500' }}>
                                                {subcontractor ? subcontractor.name : sub.supplierName}
                                            </div>
                                            {sub.laborCost > 0 && (
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    İşçilik: {sub.laborCost.toFixed(2)} ₺/adet
                                                    {stage.outputProduced.quantity > 0 && (
                                                        <div>
                                                            Toplam İşçilik: {(sub.laborCost * stage.outputProduced.quantity).toFixed(2)} ₺
                                                            ({stage.outputProduced.quantity} adet)
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontWeight: '500' }}>
                                            {stage.outputProduced.quantity} {stage.outputProduced.unit}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            )}

            {stage.startTime && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '12px',
                    color: '#64748b'
                }}>
                    <div>Başlangıç: {stage.startTime.toLocaleString('tr-TR')}</div>
                    {stage.endTime && <div>Bitiş: {stage.endTime.toLocaleString('tr-TR')}</div>}
                </div>
            )}

            {showCompleteModal && (
                <div style={styles.modalOverlay} onClick={() => setShowCompleteModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                {stage.name} Aşamasını Tamamla
                            </h2>
                            <button
                                onClick={() => setShowCompleteModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#94a3b8'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tamamlanan Miktar</label>
                            <input
                                type="number"
                                value={completionData.completedQuantity}
                                onChange={(e) => setCompletionData(prev => ({
                                    ...prev,
                                    completedQuantity: parseFloat(e.target.value) || 0
                                }))}
                                style={styles.input}
                                min="0"
                                step="0.01"
                            />
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {stage.outputProduced.product} ({stage.outputProduced.unit})
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Fire Miktarı</label>
                            <input
                                type="number"
                                value={completionData.wasteQuantity}
                                onChange={(e) => setCompletionData(prev => ({
                                    ...prev,
                                    wasteQuantity: parseFloat(e.target.value) || 0
                                }))}
                                style={styles.input}
                                min="0"
                                step="0.01"
                            />
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {stage.outputProduced.product} ({stage.outputProduced.unit})
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Notlar</label>
                            <textarea
                                value={completionData.notes}
                                onChange={(e) => setCompletionData(prev => ({
                                    ...prev,
                                    notes: e.target.value
                                }))}
                                style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                                placeholder="Aşama ile ilgili notlar ekleyin"
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setShowCompleteModal(false)}
                                style={{
                                    padding: '10px 20px',
                                    background: '#f1f5f9',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleCompleteStage}
                                style={{
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Aşamayı Tamamla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductionStageTracker;